import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';
import { X, Package, Truck, Loader2, Trash2, Plus, Search, Calendar, MapPin, Edit3, ChevronRight, CalendarPlus, ExternalLink, FileText, Lock } from 'lucide-react';
import { useUIStore } from '../../hooks/useUIStore';
import { useAuthStore } from '../../hooks/useAuth';
import { MasterBOLModal } from './MasterBOLModal';

interface LoadDetailDrawerProps {
    loadId: string | null;
    onClose: () => void;
    onUpdated: () => void;
}

const STATUS_COLORS: Record<string, string> = {
    draft: 'bg-slate-500/15 text-slate-400 border-slate-500/20',
    ready: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
    scheduled: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
    in_transit: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
    arrived: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    unloading: 'bg-violet-500/15 text-violet-400 border-violet-500/20',
    completed: 'bg-slate-500/15 text-slate-400 border-slate-400/20',
    cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
};

export function LoadDetailDrawer({ loadId, onClose, onUpdated }: LoadDetailDrawerProps) {
    const { user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';
    const [load, setLoad] = useState<any>(null);
    const [linkedOrders, setLinkedOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [removingPoId, setRemovingPoId] = useState<string | null>(null);

    // PO Add mode
    const [addMode, setAddMode] = useState(false);
    const [poSearch, setPoSearch] = useState('');
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [poLoading, setPoLoading] = useState(false);
    const [addingPoIds, setAddingPoIds] = useState<string[]>([]);
    const [linkedApptIds, setLinkedApptIds] = useState<string[]>([]);
    const [showBOL, setShowBOL] = useState(false);
    const [, setLocation] = useLocation();
    const { openAppointmentDetail } = useUIStore();

    const fetchLoad = useCallback(async () => {
        if (!loadId) return;
        setLoading(true);
        try {
            const data = await api.getLoad(loadId);
            setLoad(data.load);
            const mappedPOs = (data.linkedPOs || []).map((p: any) => ({
                linkId: p.linkId,
                orderId: p.poId,
                sourceType: 'PO' as const,
                displayNumber: p.customerPoNumber || p.poNumber || 'Unknown PO',
                customerPoNumber: p.customerPoNumber || p.poNumber || 'Unknown PO',
                customerName: p.customerName || p.clientName || 'Unknown',
                pallets: p.expectedPalletCount ?? p.totalSkids ?? 0,
                totalSkids: p.expectedPalletCount ?? p.totalSkids ?? 0,
                unitLabel: 'skids',
            }));
            const mappedSOs = (data.linkedSalesOrders || []).map((s: any) => ({
                linkId: s.linkId,
                orderId: s.salesOrderId,
                sourceType: 'SO' as const,
                displayNumber: s.orderNumber || 'Unknown SO',
                customerPoNumber: s.orderNumber || 'Unknown SO',
                customerName: s.customerName || 'Unknown',
                pallets: s.expectedPalletCount ?? s.itemsCount ?? 0,
                totalSkids: s.expectedPalletCount ?? s.itemsCount ?? 0,
                unitLabel: 'items',
            }));
            setLinkedOrders([...mappedPOs, ...mappedSOs]);
        } catch (err: any) {
            toast.error(err.message || 'Failed to load details');
        } finally {
            setLoading(false);
        }
    }, [loadId]);

    useEffect(() => {
        fetchLoad();
        // Fetch linked appointment IDs only
        if (loadId) {
            api.request<{ appointments: { appointmentId: string }[] }>(`/loads/${loadId}/appointment`)
                .then(res => setLinkedApptIds((res.appointments || []).map(a => a.appointmentId)))
                .catch(() => setLinkedApptIds([]));
        }
    }, [fetchLoad, loadId]);

    const handleOpenLinkedAppointment = async (appointmentId: string) => {
        try {
            // Fetch full appointment via canonical endpoint
            const token = useAuthStore.getState().token;
            if (!token) {
                toast.error('Session expired. Please log in again.');
                return;
            }
            const res = await fetch(`/api/appointments/${appointmentId}/details`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) {
                if (res.status === 404) {
                    toast.error('Linked appointment no longer exists.');
                    // Refresh load data to clear stale link
                    setLinkedApptIds(prev => prev.filter(id => id !== appointmentId));
                    return;
                }
                throw new Error(`Failed to load appointment (${res.status})`);
            }
            const detail = await res.json();
            const appt = detail?.appointment;
            if (!appt?.id || !appt?.status) {
                toast.error('Appointment data is incomplete.');
                return;
            }
            onClose();
            openAppointmentDetail({
                id: appt.id,
                po_number: appt.customerPoNumber || '',
                carrier_name: appt.carrierName || '',
                status: appt.status,
                scheduled_time: appt.startAt,
                dock_id: appt.dockId || null,
            });
        } catch (err: any) {
            toast.error(err.message || 'Failed to open appointment');
        }
    };

    const handleRemoveOrder = async (orderId: string, sourceType: string) => {
        if (!loadId || sourceType !== 'PO') return;
        setRemovingPoId(orderId);
        try {
            await api.removeLoadPO(loadId, orderId);
            toast.success('PO removed from load');
            fetchLoad();
            onUpdated();
        } catch (err: any) {
            toast.error(err.message || 'Failed to remove PO');
        } finally {
            setRemovingPoId(null);
        }
    };

    const searchPOs = useCallback(async (q: string) => {
        setPoLoading(true);
        try {
            const poRes = await api.searchWarehousePOs(q, '');
            const loadDirection = (load?.type || '').toLowerCase();

            const normalizedPOs = (poRes.results || [])
                .filter((p: any) => !loadDirection || (p.type || 'inbound').toLowerCase() === loadDirection)
                .map((p: any) => ({
                    id: p.id,
                    sourceType: 'PO' as const,
                    displayNumber: p.customerPoNumber || p.poNumber || 'Unknown PO',
                    customerPoNumber: p.customerPoNumber || p.poNumber || 'Unknown PO',
                    customerName: p.customerName || p.clientName || 'Unknown',
                    date: p.eta || p.createdAt,
                    status: p.status,
                    type: (p.type || 'inbound').toLowerCase(),
                    pallets: p.totalSkids || 0,
                    totalSkids: p.totalSkids || 0,
                    weight: p.totalWeight || 0,
                    activeLoadId: p.activeLoadId,
                    activeLoadNumber: p.activeLoadNumber,
                }));

            setSearchResults(normalizedPOs);
        } catch (err) {
            console.error('Search error:', err);
        } finally {
            setPoLoading(false);
        }
    }, [load?.type]);

    const handleAddPOs = async () => {
        if (!loadId || addingPoIds.length === 0) return;
        try {
            const result = await api.addLoadPOs(loadId, addingPoIds);
            toast.success(`${result.added} PO(s) added to load`);
            setAddMode(false);
            setAddingPoIds([]);
            fetchLoad();
            onUpdated();
        } catch (err: any) {
            toast.error(err.message || 'Failed to add POs');
        }
    };

    const handleStatusUpdate = async (newStatus: string) => {
        if (!loadId) return;
        try {
            await api.updateLoad(loadId, { status: newStatus });
            toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
            fetchLoad();
            onUpdated();
        } catch (err: any) {
            toast.error(err.message || 'Failed to update status');
        }
    };

    const handleDelete = async () => {
        if (!loadId) return;
        if (!confirm('Delete this load? This action cannot be undone.')) return;
        try {
            await api.deleteLoad(loadId);
            toast.success('Load deleted');
            onClose();
            onUpdated();
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete load');
        }
    };

    if (!loadId) return null;

    const statusColor = STATUS_COLORS[load?.status || 'draft'] || STATUS_COLORS.draft;
    const isEditable = isOperationsEntitled && (load?.status === 'draft' || load?.status === 'ready');
    const linkedPoIds = new Set(linkedOrders.map(p => p.orderId));
    const filteredSearchResults = searchResults.filter(p => !linkedPoIds.has(p.id) && !p.activeLoadId);
    const takenSearchResults = searchResults.filter(p => !linkedPoIds.has(p.id) && p.activeLoadId);

    return (
        <>
        <div className="fixed inset-0 z-[200] flex justify-end">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl h-full overflow-y-auto animate-slide-in-right">
                {loading && !load ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="animate-spin text-cyan-500" size={28} />
                    </div>
                ) : load ? (
                    <>
                        {/* Header */}
                        <div className="sticky top-0 bg-white dark:bg-slate-900 z-10 border-b border-slate-200 dark:border-slate-800 p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white font-mono flex items-center gap-2">
                                        <Truck className="text-cyan-500" size={20} />
                                        {load.loadNumber}
                                    </h2>
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase border ${statusColor}`}>
                                            {(load.status || 'draft').replace('_', ' ')}
                                        </span>
                                        <span className="text-xs text-slate-400">
                                            {load.type === 'inbound' ? '📥 Inbound' : '📤 Outbound'}
                                        </span>
                                    </div>
                                </div>
                                <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white p-1">
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Read-only notice for Starter accounts */}
                            {!isOperationsEntitled && (
                                <div
                                    data-testid="load-drawer-readonly-banner"
                                    className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/25 text-xs text-slate-700 dark:text-slate-300 space-y-1"
                                >
                                    <div className="flex items-center gap-1.5 font-bold text-amber-600 dark:text-amber-400">
                                        <Lock size={13} />
                                        Read-Only Historical Load (Starter Plan)
                                    </div>
                                    <p className="text-[11px] text-slate-600 dark:text-slate-400">
                                        Editing loads, linking/unlinking POs, and scheduling load appointments require the Operations plan ($199/mo). Contact{' '}
                                        <a href="mailto:support@baywhiz.com" className="underline font-semibold text-amber-600 dark:text-amber-400">
                                            support@baywhiz.com
                                        </a>{' '}
                                        to upgrade.
                                    </p>
                                </div>
                            )}

                            {/* Status quick-actions */}
                            {isEditable && (
                                <div className="flex gap-2 mt-3">
                                    {load.status === 'draft' && (
                                        <button
                                            onClick={() => handleStatusUpdate('ready')}
                                            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/20 hover:bg-blue-500/25 transition-colors"
                                        >
                                            Mark Ready
                                        </button>
                                    )}
                                    <button
                                        onClick={handleDelete}
                                        className="px-3 py-1.5 text-xs font-bold rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors ml-auto"
                                    >
                                        <Trash2 size={12} className="inline mr-1" /> Delete
                                    </button>
                                </div>
                            )}

                            {/* Schedule Appointment button */}
                            {isOperationsEntitled && linkedOrders.length > 0 && linkedApptIds.length === 0 && !['completed', 'cancelled'].includes(load?.status || '') && (
                                <button
                                    onClick={() => {
                                        onClose();
                                        setLocation(`/appointments/new?loadId=${loadId}`);
                                    }}
                                    className="w-full mt-3 px-4 py-2.5 text-sm font-bold rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-500/20"
                                >
                                    <CalendarPlus size={16} /> Schedule Appointment
                                </button>
                            )}

                            {/* Linked Appointment */}
                            {linkedApptIds.length > 0 && (
                                <div className="mt-3 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
                                    <div className="text-[10px] text-indigo-400 font-bold uppercase mb-1.5">Linked Appointment</div>
                                    {linkedApptIds.map(apptId => (
                                        <div
                                            key={apptId}
                                            className="flex items-center justify-between cursor-pointer hover:bg-indigo-500/10 rounded-lg px-2 py-1.5 -mx-2 transition-colors"
                                            onClick={() => handleOpenLinkedAppointment(apptId)}
                                        >
                                            <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                                                {apptId.replace('appt_', '').slice(0, 8)}…
                                            </div>
                                            <ExternalLink size={14} className="text-indigo-400 flex-shrink-0" />
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Generate Master BOL */}
                            {linkedOrders.length > 0 && (
                                <button
                                    onClick={() => setShowBOL(true)}
                                    className="w-full mt-3 px-4 py-2 text-sm font-bold rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 transition-all flex items-center justify-center gap-2"
                                >
                                    <FileText size={15} /> Generate Master BOL
                                </button>
                            )}
                        </div>

                        {/* Carrier Info */}
                        <div className="p-5 border-b border-slate-200 dark:border-slate-800">
                            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Carrier & Transport</h3>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                {[
                                    { label: 'Carrier', value: load.carrierName },
                                    { label: 'MC #', value: load.carrierMcNumber },
                                    { label: 'Driver', value: load.driverName },
                                    { label: 'Truck #', value: load.truckNumber },
                                    { label: 'Trailer #', value: load.trailerNumber },
                                    { label: 'Trailer Type', value: load.trailerType },
                                ].map(item => (
                                    <div key={item.label}>
                                        <span className="text-xs text-slate-400">{item.label}</span>
                                        <div className="text-slate-900 dark:text-white font-medium">{item.value || '—'}</div>
                                    </div>
                                ))}
                            </div>
                            {load.eta && (
                                <div className="mt-3 flex items-center gap-2 text-sm">
                                    <Calendar size={14} className="text-slate-400" />
                                    <span className="text-slate-500">ETA:</span>
                                    <span className="text-slate-900 dark:text-white font-medium">
                                        {(() => {
                                            return load.eta.substring(0, 10);
                                        })()}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Aggregates */}
                        <div className="flex gap-4 px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                            <div className="text-center flex-1">
                                <div className="text-xl font-bold text-slate-900 dark:text-white font-mono">{linkedOrders.length}</div>
                                <div className="text-[10px] text-slate-500 uppercase">Orders</div>
                            </div>
                            <div className="text-center flex-1">
                                <div className="text-xl font-bold text-slate-900 dark:text-white font-mono">{load.totalPallets || 0}</div>
                                <div className="text-[10px] text-slate-500 uppercase">Pallets</div>
                            </div>
                            <div className="text-center flex-1">
                                <div className="text-xl font-bold text-slate-900 dark:text-white font-mono">{(load.totalWeight || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-slate-500 uppercase">lbs</div>
                            </div>
                        </div>

                        {/* Linked POs */}
                        <div className="p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Linked Orders ({linkedOrders.length})
                                </h3>
                                {isEditable && (
                                    <button
                                        onClick={() => { setAddMode(!addMode); if (!addMode) searchPOs(''); }}
                                        className="text-xs font-bold text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
                                    >
                                        <Plus size={14} /> Add PO
                                    </button>
                                )}
                            </div>

                            {/* Add PO inline */}
                            {addMode && (
                                <div className="mb-4 p-3 bg-cyan-500/5 border border-cyan-500/20 rounded-lg">
                                    <div className="relative mb-2">
                                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                        <input
                                            type="text"
                                            value={poSearch}
                                            onChange={(e) => { setPoSearch(e.target.value); searchPOs(e.target.value); }}
                                            placeholder="Search PO..."
                                            className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none text-slate-900 dark:text-white"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="max-h-32 overflow-y-auto space-y-1">
                                        {filteredSearchResults.slice(0, 8).map(po => {
                                            const isSelected = addingPoIds.includes(po.id);
                                            return (
                                                <button
                                                    key={po.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setAddingPoIds(prev =>
                                                            isSelected ? prev.filter(id => id !== po.id) : [...prev, po.id]
                                                        );
                                                    }}
                                                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${isSelected
                                                        ? 'bg-cyan-500/15 border border-cyan-500/30'
                                                        : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                                                        }`}
                                                >
                                                    <span className="font-mono font-bold text-slate-900 dark:text-white">{po.customerPoNumber}</span>
                                                    <span className="text-slate-400">{po.totalSkids || 0} skids</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {/* Taken POs - disabled with reason */}
                                    {takenSearchResults.length > 0 && (
                                        <div className="space-y-1 mt-1">
                                            {takenSearchResults.slice(0, 4).map(po => (
                                                <div
                                                    key={po.id}
                                                    className="w-full flex items-center justify-between px-2 py-1.5 rounded text-xs opacity-50 cursor-not-allowed"
                                                >
                                                    <span className="font-mono font-bold text-slate-500">{po.customerPoNumber}</span>
                                                    <span className="text-[10px] font-bold text-amber-500">Linked to {po.activeLoadNumber}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {addingPoIds.length > 0 && (
                                        <button
                                            onClick={handleAddPOs}
                                            className="mt-2 w-full py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold transition-colors"
                                        >
                                            Add {addingPoIds.length} PO(s)
                                        </button>
                                    )}
                                </div>
                            )}

                            {linkedOrders.length === 0 ? (
                                <div className="text-center py-8 text-slate-400 text-sm">
                                    <Package className="mx-auto mb-2 text-slate-500" size={24} />
                                    No Orders linked yet
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {linkedOrders.map(po => (
                                        <div key={po.linkId} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg group">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <Package size={14} className="text-cyan-400 shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="font-mono font-bold text-sm text-slate-900 dark:text-white truncate">
                                                        {po.displayNumber || po.customerPoNumber}
                                                    </div>
                                                    <div className="text-xs text-slate-400 truncate">
                                                        {po.customerName || po.clientName || 'Unknown'} • {po.totalSkids || 0} {po.unitLabel || (po.sourceType === 'SO' ? 'items' : 'skids')}
                                                    </div>
                                                </div>
                                            </div>
                                            {isEditable && po.sourceType === 'PO' ? (
                                                <button
                                                    data-testid={`remove-load-po-${po.orderId}`}
                                                    onClick={() => handleRemoveOrder(po.orderId, po.sourceType)}
                                                    disabled={removingPoId === po.orderId}
                                                    title="Remove PO from load"
                                                    className="text-slate-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1"
                                                >
                                                    {removingPoId === po.orderId ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : (
                                                        <X size={14} />
                                                    )}
                                                </button>
                                            ) : po.sourceType === 'SO' ? (
                                                <span
                                                    data-testid={`linked-so-badge-${po.orderId}`}
                                                    title="Sales Orders are linked when creating a load and cannot be unlinked individually."
                                                    className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-200/70 dark:bg-slate-700/70 text-slate-600 dark:text-slate-300 shrink-0"
                                                >
                                                    SO • Linked at creation
                                                </span>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Notes */}
                        {load.notes && (
                            <div className="px-5 pb-5">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Notes</h3>
                                <p className="text-sm text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                                    {load.notes}
                                </p>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>

        {/* Master BOL Modal */}
        {showBOL && loadId && (
            <MasterBOLModal loadId={loadId} onClose={() => setShowBOL(false)} />
        )}
        </>
    );
}
